import { useEffect, useState } from 'react';

export interface ChartTheme {
  text: string;
  grid: string;
  accent: string;
  accentDim: string;
}

function read(varName: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return v || fallback;
}

/** Convert a #rrggbb (or #rgb) color to an rgba() string canvas can render.
 * Passes through any non-hex value (rgb/rgba/named) unchanged. */
function toRgba(color: string, alpha: number): string {
  const hex = color.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (full.length === 6 && /^[0-9a-fA-F]{6}$/.test(full)) {
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}

function computeTheme(): ChartTheme {
  const accent = read('--accent-saphir', '#2b5a9e');
  return {
    text: read('--text-secondary', '#6b6560'),
    grid: read('--border-subtle', '#ede8df'),
    accent,
    accentDim: toRgba(accent, 0.25),
  };
}

/** Resolve chart colors from trilirivos CSS vars; re-resolve on theme change. */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(computeTheme);

  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(computeTheme()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    return () => obs.disconnect();
  }, []);

  return theme;
}
